import React from 'react';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import Table, { TableColumn } from '@hero/ui-kit/tables/Table';
import useMarkTemplates from '../../api/useMarkTemplates';
import useDebouncedValue from '../../components/BlockEditor/useDebouncedValue';
import usePagerProps from '../../../../hooks/usePagerProps';
import QueryInlineError from '../../../../components/QueryInlineError';
import { MarkPageHeader } from '../../components/MarkLayout';
import { MarkButton, MarkInput, MarkLabel } from '../../components/MarkControls';
import type { MarkTemplateRow } from '../../types';
import * as layout from '../../components/markLayout.module.css';

const Templates: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const name = params.get('name') ?? '';
    const debouncedName = useDebouncedValue(name, 400);
    const offset = Math.max(Number(params.get('offset')) || 0, 0);
    const limit = Math.min(Math.max(Number(params.get('limit')) || 20, 1), 1000);
    const query = useMarkTemplates({ library: true, name: debouncedName, offset, limit });
    const pager = usePagerProps({ total_count: query.data?.data.total_count ?? 0, offset, limit });
    const state = { from: `${location.pathname}${location.search}` };
    const columns: TableColumn<MarkTemplateRow>[] = [
        {
            colKey: 'name',
            name: 'Name',
            render: (row) => (
                <Link to={`/mark/templates/${row.id}`} state={state}>
                    {row.name}
                </Link>
            )
        },
        {
            colKey: 'format',
            name: 'Type',
            render: (row) => <span>{row.format === 'html' ? 'HTML' : 'Blocks'}</span>
        },
        { colKey: 'subject', name: 'Subject', render: (row) => <span>{row.subject}</span> },
        {
            colKey: 'actions',
            name: 'Actions',
            render: (row) => (
                <MarkButton
                    aria-label={`Duplicate ${row.name ?? row.slug}`}
                    onClick={() => navigate(`/mark/templates/new?duplicate=${row.id}`, { state })}
                >
                    Duplicate
                </MarkButton>
            )
        }
    ];
    return (
        <Section className={layout.page} subtheme={['regular', 'background']} noDefaultPadding>
            <MarkPageHeader title="Templates">
                <MarkButton
                    variant="primary"
                    onClick={() => navigate('/mark/templates/new', { state })}
                >
                    Create template
                </MarkButton>
            </MarkPageHeader>
            <MarkLabel htmlFor="template-name-filter">Filter by name</MarkLabel>
            <MarkInput
                id="template-name-filter"
                type="search"
                value={name}
                onChange={(event) => {
                    const next = new URLSearchParams(params);
                    if (event.currentTarget.value) next.set('name', event.currentTarget.value);
                    else next.delete('name');
                    next.delete('offset');
                    setParams(next, { replace: true });
                }}
            />
            {query.error ? (
                <QueryInlineError error={query.error} />
            ) : (
                <Table<MarkTemplateRow>
                    columns={columns}
                    data={query.data?.data.templates ?? []}
                    rowKey="id"
                    isLoading={query.isFetching}
                    pagerProps={pager}
                    emptyValues="-"
                />
            )}
            {!query.isLoading && !query.error && query.data?.data.total_count === 0 && (
                <p>No templates found.</p>
            )}
        </Section>
    );
};

export default Templates;
